// RegistrationPage — the /registration SPA route (RFC-0015 Gap C/D).
//
// Kratos redirects a FIRST-TIME OIDC user to `${PORTAL_PUBLIC_ORIGIN}/registration`
// (kratos.yml flows.registration.ui_url). Before RFC-0015 the SPA had no such
// route, so Kratos's redirect fell through to the auth-gated shell's catch-all —
// a spurious `return_to=%2Fregistration` and a 404/skeleton flash on every new
// sign-in. This route closes that gap.
//
// Per PRD-0001:181 the portal has NO self-serve registration form: identity is
// auto-provisioned from the Google OIDC claims (email + name.first/name.last are
// always supplied by the claim mapper). This page therefore shows NO form. It
// transparently continues the Kratos registration flow and, once auth is
// established, resolves the post-auth destination in priority order and
// client-navigates there:
//
//   (1) URL `return_to`   (sanitized via sanitizeReturnTo)   — Gap D primary
//   (2) the client stash  (consumePendingAuthDestination)    — Gap D fallback
//   (3) `/`                                                    — last resort
//
// It renders OUTSIDE PortalLayout / AuthProvider (App.tsx), so it never fires
// `GET /api/me`; the destination it navigates INTO is what loads the shell.
//
// Auto-continue mechanics (RFC-0015 binding decision #3 — design is agnostic,
// verified live in Phase 3): in the OIDC-registration happy path Kratos completes
// provisioning server-side on the OIDC callback (all required traits present from
// the Google claims) and the session cookie is already set by the time the browser
// lands here. This page's job is then purely to resolve the deep-link destination
// client-side and navigate into the now-authenticated shell. It fetches the
// registration flow only to surface a DEFENSIVE error state (never a form) in the
// expected-never case where Kratos reports the flow cannot complete without more
// input. If Phase 3's live-Kratos run shows an explicit client re-submit is ever
// required, that submit slots into the `working` branch without changing this
// route's public contract.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  collectFlowMessages,
  fetchRegistrationFlow,
  LoginFlowError,
  sanitizeReturnTo,
} from '@/lib/auth-api';
import { consumePendingAuthDestination } from '@/lib/auth-destination';

type Status = 'working' | 'error';

/**
 * Resolve the post-registration destination (RFC-0015 Gap D). PURE + exported so
 * the priority order is unit-testable without a DOM: the URL `return_to` wins when
 * it sanitizes to a safe relative path; otherwise the stashed value (already
 * sanitized on read, re-sanitized here defensively); otherwise `/`.
 */
export function resolvePostRegistrationDestination(
  urlReturnTo: string | null | undefined,
  stashedReturnTo: string | null | undefined,
): string {
  return sanitizeReturnTo(urlReturnTo) ?? sanitizeReturnTo(stashedReturnTo) ?? '/';
}

export function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Kratos redirects here as `/registration?flow=<id>` (the flow id) and may echo
  // a `return_to`.
  const flowId = searchParams.get('flow');
  const urlReturnTo = searchParams.get('return_to');

  const [status, setStatus] = useState<Status>('working');
  // One-shot guard: consume() clears the stash, so the continue effect must run
  // exactly once even under React 19 StrictMode double-invocation.
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;

    let cancelled = false;

    async function continueRegistration() {
      // Defensive pre-check: if Kratos reports the flow cannot complete without
      // further input, show a (non-form) error rather than navigating into a
      // shell that would immediately 401. Expected never (traits always supplied).
      if (flowId) {
        try {
          const flow = await fetchRegistrationFlow(flowId);
          const hasError = collectFlowMessages(flow).some((m) => m.type === 'error');
          if (hasError) {
            if (!cancelled) {
              // RFC-0015 §Logging: WARN when the flow cannot complete.
              console.warn('[auth] registration flow cannot complete', { flow_id: flowId });
              setStatus('error');
            }
            return;
          }
        } catch (err) {
          // A consumed/expired flow id (auto-provision already done) throws here;
          // that is NOT a failure — fall through to resolve + navigate.
          if (!(err instanceof LoginFlowError)) throw err;
        }
      }

      if (cancelled) return;

      // Auth is established (server-side auto-provision). Resolve the destination
      // and continue into the shell.
      const stashed = consumePendingAuthDestination();
      const urlSafe = sanitizeReturnTo(urlReturnTo);
      const stashSafe = sanitizeReturnTo(stashed?.returnTo);
      const destination = resolvePostRegistrationDestination(urlReturnTo, stashed?.returnTo);
      const destinationSource = urlSafe ? 'url' : stashSafe ? 'stash' : 'default';

      // RFC-0015 §Logging: INFO with destination_source on completion.
      console.info('[auth] registration destination resolved', {
        destination_source: destinationSource,
      });

      navigate(destination, { replace: true });
    }

    void continueRegistration().catch(() => {
      if (!cancelled) setStatus('error');
    });

    return () => {
      cancelled = true;
    };
  }, [flowId, urlReturnTo, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-8 shadow-(--c-shadow-md)">
        <div className="mb-6 flex flex-col gap-1.5 text-center">
          <h1 className="text-lg font-semibold text-text">Documentation Portal</h1>
        </div>

        {status === 'working' && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 py-4 text-[13px] text-muted"
          >
            <span className="h-3.5 w-3.5 shrink-0 animate-spin-slow rounded-full border-2 border-current border-t-transparent" />
            Finishing sign-in…
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col gap-4">
            <div
              role="alert"
              className="rounded-lg border border-[rgba(255,92,92,0.2)] bg-error-bg px-3 py-2 text-[13px] text-badge-error-text"
            >
              We couldn&rsquo;t finish setting up your account. Please try signing in again.
            </div>
            <Link
              to="/login"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
            >
              Back to sign-in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
